import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import { StatusChip } from './ui';

/** The read-side fields the API adds to a product's pricing while a price change waits for store approval (flag `price_change_review`). */
export interface PriceReviewFields {
  priceReview?: { status: 'pending'; itemId: string; dueAt: string | null; approvedFrom: string };
  approvedPrice?: number | null;
  approvedCompareAtPrice?: number | null;
  approvedInstantRebate?: number | null;
}

interface PendingReviewChipProps {
  pricing?: PriceReviewFields | null;
  sx?: React.ComponentProps<typeof StatusChip>['sx'];
}

/**
 * "Pending review" chip: shown only when the API says a change is waiting for the
 * store's approval. The tooltip names the price the connected platform still shows.
 * Renders nothing otherwise, so a store with review off sees no change.
 */
export const PendingReviewChip: React.FC<PendingReviewChipProps> = ({ pricing, sx }) =>
  pricing?.priceReview?.status === 'pending' ? <PendingChip pricing={pricing as Required<Pick<PriceReviewFields, 'priceReview'>> & PriceReviewFields} sx={sx} /> : null;

// Split so the locale and translation hooks only run for a product that is actually pending.
const PendingChip: React.FC<{ pricing: Required<Pick<PriceReviewFields, 'priceReview'>> & PriceReviewFields; sx?: PendingReviewChipProps['sx'] }> = ({ pricing, sx }) => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useLocale();

  const approved = pricing.approvedPrice;
  const due = pricing.priceReview.dueAt ? formatDate(new Date(pricing.priceReview.dueAt), { month: 'short', day: 'numeric' }) : '';
  const lines = [
    typeof approved === 'number' && approved > 0
      ? t('pricing.pendingReview.approvedPrice', { price: formatCurrency(approved) })
      : t('pricing.pendingReview.noApprovedPrice'),
    typeof pricing.approvedInstantRebate === 'number' && pricing.approvedInstantRebate > 0
      ? t('pricing.pendingReview.approvedRebate', { amount: formatCurrency(pricing.approvedInstantRebate) })
      : '',
    due ? t('pricing.pendingReview.due', { date: due }) : '',
  ].filter(Boolean);

  return (
    <Tooltip title={lines.join(' ')} arrow placement="top">
      {/* StatusChip is not forwardRef; the span carries the Tooltip listeners. */}
      <Box component="span" data-testid="pending-review-chip" sx={{ display: 'inline-flex' }}>
        <StatusChip label={t('pricing.pendingReview.label')} tone="caution" size="small" sx={{ height: 20, fontSize: '0.625rem', ...sx }} />
      </Box>
    </Tooltip>
  );
};

export default PendingReviewChip;
